import { cloneElement, forwardRef, isValidElement, useId } from 'react';
import { cn } from '@/lib/cn';

const base =
  'h-8 w-full rounded-[var(--radius-sm)] border border-[var(--color-border-strong)] bg-[var(--color-canvas)] ' +
  'px-2.5 text-[13px] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] ' +
  'transition-colors focus:border-[var(--color-accent)] disabled:opacity-50';

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(base, className)} {...props} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(base, 'h-auto min-h-24 resize-y py-2 leading-relaxed', className)}
        {...props}
      />
    );
  },
);

/** Native select, styled to match. Adequate for dense filter bars. */
export const Select = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn(base, 'cursor-pointer pr-7', className)} {...props}>
        {children}
      </select>
    );
  },
);

export function Label({ className, children, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn('mb-1 block text-[12px] font-medium text-[var(--color-text-muted)]', className)}
      {...props}
    >
      {children}
    </label>
  );
}

/**
 * Labelled form field.
 *
 * The label is genuinely associated with its control via `htmlFor`/`id`, and
 * the hint or error is linked through `aria-describedby`. That association is
 * what lets assistive technology (and tests) resolve a control by its label,
 * so it is done here once rather than relied on at every call site.
 */
export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label?: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const generatedId = useId();
  const describedById = `${generatedId}-description`;

  // Attach the id to the single control this field wraps, unless the caller
  // already set one explicitly.
  const control =
    isValidElement(children) && label
      ? cloneElement(children as React.ReactElement<Record<string, unknown>>, {
          id: (children.props as { id?: string }).id ?? generatedId,
          ...(hint || error ? { 'aria-describedby': describedById } : {}),
          ...(error ? { 'aria-invalid': true } : {}),
        })
      : children;

  return (
    <div className={className}>
      {label ? <Label htmlFor={generatedId}>{label}</Label> : null}
      {control}
      {error ? (
        <p id={describedById} className="mt-1 text-[12px] text-[var(--color-negative)]">
          {error}
        </p>
      ) : hint ? (
        <p id={describedById} className="mt-1 text-[12px] text-[var(--color-text-subtle)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
